import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { toast } from '../../lib/toast'
import { StateMark, stateFromKyc } from '../../lib/StateMaterial'
import { OrgCrest } from './OrgCrest'
import { BareState } from './BareState'
import { useIdentity } from '../../lib/identity'
import { catalogExit } from '../../lib/catalogExit'
import { orgErrorCode, orgErrorMessage, orgKycStatusOf } from '../../lib/orgAccess'
import { orgApi, type OrgFull, type OrgPatchBody } from '../../api/endpoints'
import { api } from '../../api/client'

/**
 * OrgProfilePanel — the company record, shell-agnostic.
 *
 * ── WS-M: ROLE-SEPARATED EDITING ──────────────────────────────────────────
 * A company's LEGAL NAME and ADDRESS anchor its verification, so an org
 * owner/admin can NOT rewrite them directly — the server refuses it with
 * `change_request_required` (org/index.ts). On this screen they are READ-ONLY,
 * each with a "Request a change" action: propose a new value, attach proof, and
 * an Aska reviewer approves it. While a request is open the field shows a
 * "change pending review" note. The CONTACT NUMBER, trading name, website and
 * registration numbers stay directly editable (they are not identity anchors).
 * A regular member edits nothing.
 *
 * ── A MEMBER SEES A READ-ONLY VIEW, NEVER A 403 ───────────────────────────
 * The edit affordances simply are not drawn for a member, and the panel says
 * who to ask instead of leaving a dead end where a button used to be.
 *
 * ── AND A SUSPENDED MEMBER SEES LESS, ON PURPOSE ──────────────────────────
 * `orgViewSuspended` omits the company record. `restricted:true` is on the
 * wire so the client renders that state deliberately.
 */

type Phase = 'loading' | 'ready' | 'no_org' | 'not_member' | 'suspended' | 'unavailable' | 'error'

const REG_FIELDS = [
  ['duns', 'D-U-N-S number'],
  ['incorporation', 'Incorporation number'],
  ['sellersPermit', 'Seller’s permit'],
  ['gst', 'GST number'],
  ['vat', 'VAT number'],
  ['taxId', 'Tax ID'],
] as const

/** Fields that route through Aska approval. legalName/address are request-only
 *  for every org; displayName/website/registrationNumbers/contactPhone are
 *  request-only for SUPPLIER orgs (gated server-side on tradeRole). */
type ChangeField = 'legalName' | 'address' | 'displayName' | 'website' | 'registrationNumbers' | 'contactPhone'
/** The subset that opens the proof-upload dialog (the identity anchors). */
type DialogField = Extract<ChangeField, 'legalName' | 'address'>

interface ChangeRequestRow {
  id: string
  field: ChangeField
  proposedValue: string | Record<string, unknown>
  status: 'pending' | 'approved' | 'rejected'
  submittedAt: string
  reviewNote?: string | null
}

interface ProposedAddress {
  line1: string
  line2?: string
  city: string
  state?: string
  postalCode?: string
  country: string
  countryName?: string
}

export function OrgProfilePanel({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate()
  const identity = useIdentity()
  const [phase, setPhase] = useState<Phase>('loading')
  const [org, setOrg] = useState<OrgFull | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<OrgPatchBody>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // WS-M: the directly-editable company contact number. `GET /org` does not
  // echo it back (the read projection is out of scope for this change), so the
  // input seeds empty and is populated from a successful save; the label says
  // so plainly rather than showing a stale blank as "no number".
  const [contactPhone, setContactPhone] = useState<string>('')
  const [savedPhone, setSavedPhone] = useState<string | null>(null)
  const [phoneSaving, setPhoneSaving] = useState(false)

  // WS-M: the open/recent name+address change requests for this org.
  const [changeRequests, setChangeRequests] = useState<ChangeRequestRow[]>([])
  const [requestField, setRequestField] = useState<DialogField | null>(null)

  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) return
    const el = editing
      ? document.getElementById('org-display')
      : document.querySelector<HTMLElement>('[data-testid="org-edit"]')
    el?.focus()
  }, [editing])
  useEffect(() => { mounted.current = true }, [])

  const loadRequests = useCallback(async (orgId: string) => {
    try {
      const res = await api<{ requests: ChangeRequestRow[] }>(
        `/org/${encodeURIComponent(orgId)}/change-requests`,
      )
      setChangeRequests(res.requests || [])
    } catch {
      // A failed change-request read must never blank the whole profile — the
      // pending badges are a nicety, the record is the point.
      setChangeRequests([])
    }
  }, [])

  const load = useCallback(async () => {
    setPhase('loading'); setLoadError(null)
    try {
      const res = await orgApi.get()
      setOrg(res.org)
      const restricted = res.org.restricted || res.seatSuspended
      setPhase(restricted ? 'suspended' : 'ready')
      const canManage = res.org.viewerOrgRole === 'owner' || res.org.viewerOrgRole === 'admin'
      if (!restricted && canManage) void loadRequests(res.org.id)
    } catch (err) {
      const code = orgErrorCode(err)
      if (code === 'no_org') { setPhase('no_org'); return }
      if (code === 'not_org_member') { setPhase('not_member'); return }
      if (code === 'not_found') { setPhase('unavailable'); return }
      setLoadError(orgErrorMessage(err, 'Couldn’t load your company profile.'))
      setPhase('error')
    }
  }, [loadRequests])

  useEffect(() => {
    if (identity.status === 'loading') return
    if (!identity.signedIn) return
    void load()
  }, [load, identity.status, identity.signedIn])

  const orgName = org?.displayName || org?.legalName || 'your company'
  const canEdit = org?.viewerOrgRole === 'owner' || org?.viewerOrgRole === 'admin'
  // A SUPPLIER org's company record is reviewed by Aska, so owner/admin edits are
  // submitted as change requests rather than saved directly. Buyer orgs edit
  // directly. Mirrors the server gate (org/index.ts) keyed on tradeRole.
  const isSupplier = org?.tradeRole === 'supplier' || org?.tradeRole === 'both'

  const pendingFor = (field: ChangeField): ChangeRequestRow | undefined =>
    changeRequests.find((r) => r.field === field && r.status === 'pending')

  function beginEdit() {
    if (!org) return
    setDraft({
      displayName: org.displayName ?? '',
      website: org.website ?? '',
      registrationNumbers: { ...(org.registrationNumbers as OrgPatchBody['registrationNumbers']) },
    })
    setSaveError(null)
    setEditing(true)
  }

  async function save() {
    if (!org) return
    setSaving(true); setSaveError(null)
    try {
      // legalName / address are DELIBERATELY not in this body — they are
      // change-request-gated. Only the directly-editable fields are sent.
      const body: OrgPatchBody = {}
      const dn = (draft.displayName ?? '').trim()
      if (dn !== (org.displayName ?? '')) body.displayName = dn || null
      const ws = (draft.website ?? '').trim()
      if (ws !== (org.website ?? '')) body.website = ws || null
      const regs = draft.registrationNumbers ?? {}
      const current = (org.registrationNumbers ?? {}) as Record<string, string | null>
      const regPatch: Record<string, string | null> = {}
      for (const [key] of REG_FIELDS) {
        const v = (regs[key] ?? '') as string
        const trimmed = (v ?? '').trim()
        if (trimmed !== (current[key] ?? '')) regPatch[key] = trimmed || null
      }
      if (Object.keys(regPatch).length) body.registrationNumbers = regPatch

      if (Object.keys(body).length === 0) { setEditing(false); setSaving(false); return }

      if (isSupplier) {
        // A supplier's company record is reviewed by Aska: submit a change
        // request per changed field instead of a direct save (the server refuses
        // a direct supplier edit with `change_request_required`). Proof is
        // optional for these profile fields — a reviewer eyeballs the value.
        const reqs: Array<{ field: ChangeField; proposedValue: unknown }> = []
        if ('displayName' in body) reqs.push({ field: 'displayName', proposedValue: body.displayName ?? null })
        if ('website' in body) reqs.push({ field: 'website', proposedValue: body.website ?? null })
        if ('registrationNumbers' in body) reqs.push({ field: 'registrationNumbers', proposedValue: body.registrationNumbers })
        for (const r of reqs) {
          await api(`/org/${encodeURIComponent(org.id)}/change-request`, {
            method: 'POST', body: { ...r, proof: [] },
          })
        }
        feedback.success()
        setEditing(false)
        toast('Changes submitted — our team reviews them before they go live.', 'success')
        void loadRequests(org.id)
        return
      }

      const res = await orgApi.patch(body)
      setOrg(res.org)
      feedback.success()
      setEditing(false)
      toast('Company profile saved.', 'success')
    } catch (err) {
      feedback.deny()
      setSaveError(orgErrorMessage(err, 'Couldn’t save those changes.'))
    } finally {
      setSaving(false)
    }
  }

  async function saveContactNumber() {
    if (!org) return
    setPhoneSaving(true)
    try {
      const phone = contactPhone.trim() || null
      if (isSupplier) {
        // Supplier contact number is part of the reviewed record — submit it as a
        // change request (the direct endpoint refuses a supplier with
        // `change_request_required`).
        await api(`/org/${encodeURIComponent(org.id)}/change-request`, {
          method: 'POST', body: { field: 'contactPhone', proposedValue: phone, proof: [] },
        })
        feedback.success()
        toast('Contact number submitted for review.', 'success')
        void loadRequests(org.id)
        return
      }
      const res = await api<{ ok: boolean; phone: string | null }>(
        `/org/${encodeURIComponent(org.id)}/contact-number`,
        { method: 'PATCH', body: { phone } },
      )
      setSavedPhone(res.phone)
      setContactPhone(res.phone ?? '')
      feedback.success()
      toast(res.phone ? 'Contact number updated.' : 'Contact number cleared.', 'success')
    } catch (err) {
      feedback.deny()
      toast(orgErrorMessage(err, 'Couldn’t update the contact number.'), 'error')
    } finally {
      setPhoneSaving(false)
    }
  }

  const catalog = catalogExit(identity.signedIn)
  const browseAction = { label: catalog.label, onClick: () => navigate(catalog.path) }

  if (identity.status === 'anon' || identity.status === 'unavailable') {
    return (
      <BareState
        title="Company"
        heading="Sign in to see your company."
        body="Your company’s record and its verification live behind your account."
        primary={{ label: 'Sign in', onClick: () => navigate('/signin') }}
        secondary={browseAction}
        testId="org-signed-out"
      />
    )
  }

  if (phase === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="org-panel">
        <div className="flex items-center gap-4">
          <div className="skeleton h-[72px] w-[72px] rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-7 w-52 rounded" />
            <div className="skeleton h-4 w-32 rounded" />
          </div>
        </div>
        <div className="rule-strong mt-6" />
        <div className="mt-5 space-y-2">
          <div className="skeleton h-14 rounded-md" />
          <div className="skeleton h-14 rounded-md" />
          <div className="skeleton h-14 rounded-md" />
        </div>
        <span className="sr-only" role="status">Loading your company profile</span>
      </div>
    )
  }

  if (phase === 'unavailable') {
    return (
      <BareState
        title="Company"
        heading="Company profiles aren’t switched on for this account yet."
        body="Your account works exactly as it does today. Nothing is missing."
        primary={browseAction}
        secondary={onBack ? { label: 'Back', onClick: onBack } : undefined}
        testId="org-unavailable"
      />
    )
  }
  if (phase === 'no_org') {
    return (
      <BareState
        title="Company"
        heading="You’re not part of a company on Diaex yet."
        body="A verified company shares one approval — everyone on the team sees prices without a review of their own."
        primary={{ label: 'Set up verification', onClick: () => navigate('/kyc') }}
        secondary={browseAction}
        testId="org-no-org"
      />
    )
  }
  if (phase === 'not_member') {
    return (
      <BareState
        title="Company"
        heading="You’re no longer part of that company."
        body="Your account still works. Prices stay locked until you join a team again or are verified on your own."
        primary={browseAction}
        secondary={{ label: 'Contact support', href: 'mailto:info@thediaex.com' }}
        testId="org-not-member"
      />
    )
  }
  if (phase === 'error') {
    return (
      <BareState
        title="Company"
        heading="Couldn’t load your company profile."
        body={loadError ?? 'Something went wrong on our side.'}
        primary={{ label: 'Try again', onClick: () => void load() }}
        secondary={browseAction}
        testId="org-load-error"
      />
    )
  }

  if (phase === 'suspended' && org) {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="org-suspended">
        <header className="flex items-start gap-4">
          <OrgCrest orgId={org.id} size={96} name={orgName} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-serif text-2xl tracking-h1 text-ink md:text-4xl">{orgName}</h1>
            <div className="mt-2">
              <StateMark state="suspended" label="Your seat is paused" testId="org-seat-state" />
            </div>
          </div>
        </header>
        <div className="rule-strong mt-6" />
        <p className="mt-6 max-w-[46ch] text-sm leading-relaxed text-soft">
          You can keep browsing — prices are hidden until an owner or admin at {orgName} switches
          your seat back on. The rest of {orgName}’s record isn’t shown while your seat is paused.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <a
            className="btn-primary inline-flex h-11 items-center justify-center px-5"
            href={`mailto:info@thediaex.com?subject=${encodeURIComponent(`Diaex — my seat at ${orgName} is paused`)}`}
            data-testid="org-suspended-contact"
          >
            Email Diaex support
          </a>
          <PressScale feel="tap" onClick={browseAction.onClick} className="btn-secondary h-11 px-5" data-testid="org-suspended-browse">
            {browseAction.label}
          </PressScale>
        </div>
      </div>
    )
  }

  if (!org) return null

  const kycState = stateFromKyc(orgKycStatusOf(org))
  const regs = (org.registrationNumbers ?? {}) as Record<string, string | null>
  const filedRegs = REG_FIELDS.filter(([k]) => regs[k])
  const legalPending = pendingFor('legalName')
  const addressPending = pendingFor('address')
  // Supplier profile fields show a "pending review" note while a request is open.
  const displayNamePending = Boolean(pendingFor('displayName'))
  const websitePending = Boolean(pendingFor('website'))
  const regPending = Boolean(pendingFor('registrationNumbers'))
  const contactPending = pendingFor('contactPhone')

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="org-panel">
      <header className="flex items-start gap-4">
        <OrgCrest orgId={org.id} size={96} name={orgName} className="crest-strike shrink-0" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-serif text-2xl tracking-h1 text-ink md:text-4xl">{orgName}</h1>
          {org.displayName && org.displayName !== org.legalName && (
            <p className="mt-0.5 truncate text-sm text-soft">{org.legalName}</p>
          )}
          <div className="mt-2">
            <StateMark state={kycState} testId="org-kyc-state" />
          </div>
        </div>
      </header>

      <p className="mt-4 text-sm leading-relaxed text-ink" data-testid="org-inherit-line">
        {kycState === 'approved'
          ? 'Everyone on this team sees prices and supplier contacts — they inherit this verification, with no review of their own.'
          : kycState === 'rejected'
            ? `Prices stay locked for everyone on this team while ${orgName}’s verification is declined.${org.kyc?.rejectionReason ? ` ${org.kyc.rejectionReason}` : ''}`
            : `Prices stay locked for everyone on this team until ${orgName} is verified — usually 1–2 business days.`}
      </p>

      <div className="rule-strong mt-5" />

      {!editing && (
        <>
          <dl className="mt-5 divide-y divide-line" data-testid="org-fields">
            {/* Legal name + address are IDENTITY ANCHORS — read-only, changed
                only through a reviewed request. */}
            <LockedField
              label="Legal name"
              value={org.legalName}
              canRequest={canEdit}
              pending={legalPending ? String(legalPending.proposedValue) : null}
              onRequest={() => setRequestField('legalName')}
              testId="org-legal-locked"
            />
            <LockedField
              label="Address"
              value={addressLine(org.address)}
              canRequest={canEdit}
              pending={addressPending ? addressLine(addressPending.proposedValue as OrgFull['address']) : null}
              onRequest={() => setRequestField('address')}
              testId="org-address-locked"
            />
            <Field label="Trading name" value={org.displayName} pending={displayNamePending} />
            <Field label="Website" value={org.website} href={org.website ?? undefined} pending={websitePending} />
            <Field label="What you do" value={tradeLabel(org.tradeRole)} />
            {filedRegs.map(([key, label], i) => (
              <Field key={key} label={label} value={regs[key]} pending={regPending && i === 0} />
            ))}
          </dl>

          {/* WS-M: the directly-editable company contact number. */}
          {canEdit && (
            <section className="mt-6" data-testid="org-contact-number">
              <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">
                Company contact number
              </h2>
              <p className="mb-2 text-[12.5px] leading-relaxed text-soft">
                Buyers reach {orgName} on this line.{' '}
                {isSupplier
                  ? 'Changes are reviewed by Aska before they go live.'
                  : 'You can change it any time — no review needed.'}
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[220px] flex-1">
                  <label htmlFor="org-contact-input" className="sr-only">Contact number</label>
                  <input
                    id="org-contact-input"
                    type="tel"
                    inputMode="tel"
                    placeholder={savedPhone ?? 'e.g. +1 212 555 0142'}
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="input h-11 w-full text-[16px]"
                    data-testid="org-contact-input"
                  />
                </div>
                <PressScale
                  feel="tap"
                  onClick={() => void saveContactNumber()}
                  disabled={phoneSaving}
                  className="btn-primary h-11 px-5"
                  data-testid="org-contact-save"
                >
                  {phoneSaving
                    ? (isSupplier ? 'Submitting…' : 'Saving…')
                    : (isSupplier ? 'Submit for review' : 'Save number')}
                </PressScale>
              </div>
              {!isSupplier && savedPhone !== null && (
                <p className="mt-2 text-[12.5px] text-soft" data-testid="org-contact-saved">
                  Current number: <span className="text-ink">{savedPhone || 'none'}</span>
                </p>
              )}
              {isSupplier && contactPending && (
                <p className="mt-2 text-[12px] text-amber-700" data-testid="org-contact-pending">
                  A new contact number is pending review.
                </p>
              )}
            </section>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            {canEdit ? (
              <PressScale feel="tap" onClick={beginEdit} className="btn-primary h-11 px-5" data-testid="org-edit">
                Edit company details
              </PressScale>
            ) : (
              <p className="text-[12.5px] leading-relaxed text-soft" data-testid="org-readonly-note">
                Only an owner or admin can change {orgName}’s details. Ask one of them, or open
                {' '}Team to see who they are.
              </p>
            )}
            <PressScale feel="tap" onClick={() => navigate('/account/team')} className="btn-secondary h-11 px-5" data-testid="org-team-link">
              Team
            </PressScale>
          </div>
        </>
      )}

      {editing && (
        <form
          className="mt-5 space-y-4"
          onSubmit={(e) => { e.preventDefault(); void save() }}
          data-testid="org-edit-form"
        >
          {/* Legal name is NOT editable here — a read-only reminder points at the
              request flow instead of offering a field the server would refuse. */}
          <p className="rounded-md border border-line bg-paper-2 px-3 py-2 text-[12.5px] leading-relaxed text-soft" data-testid="org-edit-anchor-note">
            Legal name and address can only be changed through a reviewed request — close this form and use
            {' '}“Request a change”.
            {isSupplier ? ' Changes to your company details below are also reviewed by Aska before they go live.' : ''}
          </p>
          <Input id="org-display" label="Trading name" hint="What buyers and suppliers see." value={(draft.displayName ?? '') as string} onChange={(v) => setDraft((d) => ({ ...d, displayName: v }))} />
          <Input id="org-website" label="Website" inputMode="url" value={(draft.website ?? '') as string} onChange={(v) => setDraft((d) => ({ ...d, website: v }))} />
          <fieldset className="space-y-3">
            <legend className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">
              Registration numbers
            </legend>
            {REG_FIELDS.map(([key, label]) => (
              <Input
                key={key}
                id={`org-reg-${key}`}
                label={label}
                value={((draft.registrationNumbers ?? {})[key] ?? '') as string}
                onChange={(v) => setDraft((d) => ({
                  ...d,
                  registrationNumbers: { ...(d.registrationNumbers ?? {}), [key]: v },
                }))}
              />
            ))}
          </fieldset>

          {saveError && (
            <p className="text-[13px] font-medium text-danger" role="alert" data-testid="org-save-error">{saveError}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <PressScale feel="tap" type="submit" disabled={saving} className="btn-primary h-11 px-5" data-testid="org-save">
              {saving
                ? (isSupplier ? 'Submitting…' : 'Saving…')
                : (isSupplier ? 'Submit for review' : 'Save changes')}
            </PressScale>
            <PressScale feel="dim" onClick={() => { setEditing(false); setSaveError(null) }} className="btn-secondary h-11 px-5" data-testid="org-cancel">
              Cancel
            </PressScale>
          </div>
        </form>
      )}

      {requestField && (
        <ChangeRequestDialog
          orgId={org.id}
          field={requestField}
          currentLegalName={org.legalName}
          onClose={() => setRequestField(null)}
          onSubmitted={() => { setRequestField(null); void loadRequests(org.id) }}
        />
      )}
    </div>
  )
}

/* ── the change-request dialog ──────────────────────────────────────────────
 * Propose a new legalName or address + attach proof (the SAME signed-URL
 * pattern KYC uses: POST upload-url → PUT bytes → carry the objectPath), then
 * POST the request. The org owner cannot apply it — a reviewer does.
 */
function ChangeRequestDialog({
  orgId, field, currentLegalName, onClose, onSubmitted,
}: {
  orgId: string
  field: DialogField
  currentLegalName: string
  onClose: () => void
  onSubmitted: () => void
}) {
  const [legalName, setLegalName] = useState('')
  const [addr, setAddr] = useState<ProposedAddress>({ line1: '', city: '', country: '' })
  const [proof, setProof] = useState<Array<{ objectPath: string; fileName: string; sizeBytes?: number }>>([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onPickFile(file: File) {
    setUploading(true); setError(null)
    try {
      const contentType = file.type || 'application/octet-stream'
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
      const { uploadUrl, objectPath } = await api<{ uploadUrl: string; objectPath: string }>(
        `/org/${encodeURIComponent(orgId)}/change-request/upload-url`,
        { method: 'POST', body: { contentType, ext } },
      )
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
      if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`)
      setProof((p) => [...p, { objectPath, fileName: file.name, sizeBytes: file.size }])
    } catch (err) {
      setError(orgErrorMessage(err, 'Couldn’t upload that file. Try again.'))
    } finally {
      setUploading(false)
    }
  }

  async function submit() {
    setError(null)
    if (proof.length === 0) { setError('Attach at least one proof document.'); return }
    if (field === 'legalName' && !legalName.trim()) { setError('Enter the new legal name.'); return }
    if (field === 'address' && (!addr.line1.trim() || !addr.city.trim() || !/^[A-Za-z]{2}$/.test(addr.country.trim()))) {
      setError('Fill in line 1, city and a 2-letter country code.'); return
    }
    setSubmitting(true)
    try {
      const proposedValue = field === 'legalName'
        ? legalName.trim()
        : {
            line1: addr.line1.trim(), city: addr.city.trim(), country: addr.country.trim().toUpperCase(),
            ...(addr.line2?.trim() ? { line2: addr.line2.trim() } : {}),
            ...(addr.state?.trim() ? { state: addr.state.trim() } : {}),
            ...(addr.postalCode?.trim() ? { postalCode: addr.postalCode.trim() } : {}),
            ...(addr.countryName?.trim() ? { countryName: addr.countryName.trim() } : {}),
          }
      await api(`/org/${encodeURIComponent(orgId)}/change-request`, {
        method: 'POST',
        body: { field, proposedValue, proof },
      })
      feedback.success()
      toast('Request sent — our team will review your documents.', 'success')
      onSubmitted()
    } catch (err) {
      feedback.deny()
      setError(orgErrorMessage(err, 'Couldn’t send that request.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-label="Request a change" data-testid="org-change-dialog">
      <div className="w-full max-w-[520px] rounded-xl border border-line bg-paper p-5 shadow-lg">
        <h2 className="font-serif text-xl tracking-h1 text-ink">
          Request a {field === 'legalName' ? 'legal-name' : 'address'} change
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-soft">
          Your {field === 'legalName' ? 'legal name' : 'registered address'} is part of your verification, so an Aska
          reviewer approves the change. Attach a document that proves it (an incorporation certificate, a utility
          bill, a lease).
        </p>

        <div className="mt-4 space-y-3">
          {field === 'legalName' ? (
            <div>
              <label htmlFor="cr-legal" className="mb-1 block text-[12.5px] text-soft">New legal name</label>
              <input id="cr-legal" className="input h-11 w-full text-[16px]" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder={currentLegalName} data-testid="cr-legal-input" />
            </div>
          ) : (
            <div className="space-y-2">
              <TinyInput id="cr-line1" label="Address line 1" value={addr.line1} onChange={(v) => setAddr((a) => ({ ...a, line1: v }))} />
              <TinyInput id="cr-line2" label="Line 2 (optional)" value={addr.line2 ?? ''} onChange={(v) => setAddr((a) => ({ ...a, line2: v }))} />
              <div className="flex gap-2">
                <TinyInput id="cr-city" label="City" value={addr.city} onChange={(v) => setAddr((a) => ({ ...a, city: v }))} />
                <TinyInput id="cr-state" label="State (optional)" value={addr.state ?? ''} onChange={(v) => setAddr((a) => ({ ...a, state: v }))} />
              </div>
              <div className="flex gap-2">
                <TinyInput id="cr-postal" label="Postal code (optional)" value={addr.postalCode ?? ''} onChange={(v) => setAddr((a) => ({ ...a, postalCode: v }))} />
                <TinyInput id="cr-country" label="Country (ISO-2, e.g. US)" value={addr.country} onChange={(v) => setAddr((a) => ({ ...a, country: v }))} />
              </div>
            </div>
          )}

          <div>
            <span className="mb-1 block text-[12.5px] text-soft">Proof documents</span>
            <label className="inline-flex h-10 cursor-pointer items-center rounded-md border border-line px-3 text-[13px] text-ink hover:bg-paper-2" data-testid="cr-upload">
              {uploading ? 'Uploading…' : 'Add a document'}
              <input
                type="file"
                className="sr-only"
                accept="application/pdf,image/*"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPickFile(f); e.target.value = '' }}
              />
            </label>
            {proof.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="cr-proof-list">
                {proof.map((p, i) => (
                  <li key={p.objectPath} className="flex items-center justify-between gap-2 text-[12.5px] text-ink">
                    <span className="truncate">{p.fileName}</span>
                    <button type="button" className="text-soft underline" onClick={() => setProof((list) => list.filter((_, j) => j !== i))}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-[13px] font-medium text-danger" role="alert" data-testid="cr-error">{error}</p>}
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <PressScale feel="dim" onClick={onClose} className="btn-secondary h-11 px-5" data-testid="cr-cancel">Cancel</PressScale>
          <PressScale feel="tap" onClick={() => void submit()} disabled={submitting || uploading} className="btn-primary h-11 px-5" data-testid="cr-submit">
            {submitting ? 'Sending…' : 'Send request'}
          </PressScale>
        </div>
      </div>
    </div>
  )
}

function tradeLabel(tradeRole: OrgFull['tradeRole']): string | null {
  switch (tradeRole) {
    case 'both': return 'Buys and sells diamonds'
    case 'buyer': return 'Buys diamonds'
    case 'supplier': return 'Sells / lists stock'
    default: return null
  }
}

function addressLine(address: OrgFull['address']): string | null {
  if (!address || typeof address !== 'object') return null
  const a = address as Record<string, unknown>
  const parts = ['line1', 'line2', 'city', 'state', 'postalCode', 'countryName']
    .map((k) => (typeof a[k] === 'string' ? (a[k] as string).trim() : ''))
    .filter(Boolean)
  if (parts.length === 0 && typeof a.country === 'string') return a.country as string
  return parts.length ? parts.join(', ') : null
}

/** An identity-anchor field: value shown read-only, with a "Request a change"
 *  action and a pending-review note when a request is open. */
function LockedField({
  label, value, canRequest, pending, onRequest, testId,
}: {
  label: string
  value: string | null | undefined
  canRequest: boolean
  pending: string | null
  onRequest: () => void
  testId: string
}) {
  return (
    <div className="flex min-h-[56px] items-baseline justify-between gap-4 py-3" data-testid={testId}>
      <dt className="shrink-0 text-[12.5px] text-soft">{label}</dt>
      <dd className="min-w-0 flex-1 text-right text-[15px] text-ink">
        {value ? <span className="break-words">{value}</span> : <span className="text-soft">Not provided</span>}
        {pending ? (
          <p className="mt-1 text-[12px] text-amber-700" data-testid={`${testId}-pending`}>
            Change to “{pending}” is pending review.
          </p>
        ) : canRequest ? (
          <button
            type="button"
            onClick={onRequest}
            className="mt-1 block w-full text-right text-[12px] text-action underline decoration-line underline-offset-2"
            data-testid={`${testId}-request`}
          >
            Request a change
          </button>
        ) : null}
      </dd>
    </div>
  )
}

function Field({ label, value, href, pending }: { label: string; value: string | null | undefined; href?: string; pending?: boolean }) {
  return (
    <div className="flex min-h-[56px] items-baseline justify-between gap-4 py-3">
      <dt className="shrink-0 text-[12.5px] text-soft">{label}</dt>
      <dd className="min-w-0 text-right text-[15px] text-ink">
        {value
          ? (href
            ? <a href={normalizeHref(href)} target="_blank" rel="noreferrer noopener" className="break-words underline decoration-line underline-offset-2">{value}</a>
            : <span className="break-words">{value}</span>)
          : <span className="text-soft">Not provided</span>}
        {pending ? (
          <p className="mt-1 text-[12px] text-amber-700" data-testid="org-field-pending">A change is pending review.</p>
        ) : null}
      </dd>
    </div>
  )
}

function normalizeHref(v: string): string {
  return /^https?:\/\//i.test(v) ? v : `https://${v}`
}

function Input({
  id, label, value, onChange, hint, type = 'text', inputMode, required,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
  type?: string
  inputMode?: 'url' | 'email' | 'text'
  required?: boolean
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[12.5px] text-soft">{label}</label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input h-11 w-full text-[16px]"
      />
      {hint && <p className="mt-1 text-[12px] text-soft">{hint}</p>}
    </div>
  )
}

function TinyInput({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex-1">
      <label htmlFor={id} className="mb-1 block text-[12px] text-soft">{label}</label>
      <input id={id} className="input h-11 w-full text-[16px]" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}
